import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCatalog } from "../lib/stremio";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Loader2 } from "lucide-react";
import { useDebounce } from "@/lib/hooks";
import { CatalogGrid } from "@/components/CatalogGrid";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { CatalogType } from "@/types/catalog";
import Aurora from "@/components/Aurora";

export function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.pathname.includes("series") ? "series" : "movie";
  const debouncedSearch = useDebounce(searchParams.get("q") || "", 500);

  const [inputValue, setInputValue] = useState("");
  useEffect(() => {
    setInputValue(searchParams.get("q") || "");
  }, []);
  useEffect(() => {
    if (inputValue && inputValue !== searchParams.get("q")) {
      setSearchParams((prev) => {
        prev.set("q", inputValue);
        return prev;
      });
    }
  }, [inputValue]);

  const {
    data: movieData,
    isLoading: isLoadingMovies,
    error: movieError,
  } = useQuery({
    queryKey: ["catalog", "movie/official", debouncedSearch],
    queryFn: async () => {
      return await getCatalog(
        "movie/official",
        debouncedSearch ? { search: debouncedSearch } : undefined
      );
    },
  });

  // Mock query for series - replace with actual API call later
  const {
    data: seriesData,
    isLoading: isLoadingSeries,
    error: seriesError,
  } = useQuery({
    queryKey: ["catalog", "series/offical", debouncedSearch],
    queryFn: async () => {
      // TODO: Implement series API
      return await getCatalog(
        "series/official",
        debouncedSearch ? { search: debouncedSearch } : undefined
      );
    },
  });

  const isLoading = activeTab === "movie" ? isLoadingMovies : isLoadingSeries;
  const error = activeTab === "movie" ? movieError : seriesError;
  const data = activeTab === "movie" ? movieData : seriesData;

  return (
    <div className="min-h-screen bg-background">
      <Aurora />
      <div className="mx-auto p-8">
        <header className="mb-8 flex gap-2">
          <div className="relative flex-1 max-w-lg w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search movies and series..."
              className="pl-10"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            {inputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1 h-7 w-7"
                onClick={() => {
                  setInputValue("");
                  setSearchParams({});
                }}
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <Tabs
            defaultValue={activeTab}
            onValueChange={(value: string) =>
              navigate(value === "movie" ? "/" : "/series")
            }
          >
            <TabsList>
              <TabsTrigger value="movie">Movies</TabsTrigger>
              <TabsTrigger value="series">Series</TabsTrigger>
            </TabsList>
          </Tabs>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-destructive/10 border-destructive rounded-lg p-6">
            <p className="text-destructive">Error: {error.message}</p>
          </div>
        ) : (
          <CatalogGrid items={data?.metas || []} type={activeTab} />
        )}
      </div>
    </div>
  );
}

export default Home;
